import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box } from '@mui/material';
import { ExpressionEditor } from 'app/shared/componentsRestyled/common/CodeEditor';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import { selectMasterCompanyId } from 'store/auth/selectors';
import { getPropertiesAction } from 'store/companies/actions';
import { selectMasterCompanyPropertiesAsObject } from 'store/companies/selectors';

import { ExplainExpressionButton } from './ExplainExpression';
import { EXPLAIN_INLINE_SCHEMA_DOMAIN } from './ExplainExpression/ExplainExpression.constants';
import { expressionMenuGroupStyles } from './ExpressionField.styles';
import { GenerateExpressionButton } from './GenerateExpression';
import { GENERATE_INLINE_SCHEMA_DOMAIN } from './GenerateExpression/GenerateExpression.constants';

const ExpressionField = ({
  setInForm,
  schemaParams,
  isOnlyBooleanAllowed,
  forceAdvancedMode = false,
  value = '',
  disabled = false,
  name = 'userFriendlyExpression',
  validateField,
  staticValidateField,
  schemaDomain = null,
  onGenerated = null,
  ...props
}) => {
  const { handleChange } = props;

  const dispatch = useDispatch();
  const masterCompanyId = useSelector(selectMasterCompanyId);
  const masterCompanyProperties = useSelector(selectMasterCompanyPropertiesAsObject);
  const areMasterCompanyPropertiesLoaded = !_.isEmpty(masterCompanyProperties);

  useEffectSafeStrictMode(() => {
    const shouldLoadProperties = !areMasterCompanyPropertiesLoaded && masterCompanyId;

    if (shouldLoadProperties) {
      dispatch(getPropertiesAction({ companyId: masterCompanyId }));
    }
  }, [areMasterCompanyPropertiesLoaded, masterCompanyId, dispatch]);

  const commonProps = useMemo(
    () => ({
      disabled,
      isOnlyBooleanAllowed,
      forceAdvancedMode,
      name,
      schemaParams,
      value,
    }),
    [disabled, isOnlyBooleanAllowed, forceAdvancedMode, name, value, schemaParams],
  );

  const onApplyGenerated = useCallback(
    newValue => {
      handleChange({ target: { name, value: newValue, dataset: { noboolean: true } } });
      onGenerated?.(newValue);
    },
    [handleChange, name, onGenerated],
  );

  const menu = (
    <Box sx={expressionMenuGroupStyles}>
      <ExplainExpressionButton
        disabled={disabled}
        isOnlyBooleanAllowed={isOnlyBooleanAllowed}
        schemaDomain={schemaDomain ?? EXPLAIN_INLINE_SCHEMA_DOMAIN}
        schemaParams={schemaParams}
        value={value}
      />
      <GenerateExpressionButton
        disabled={disabled}
        isOnlyBooleanAllowed={isOnlyBooleanAllowed}
        schemaDomain={schemaDomain ?? GENERATE_INLINE_SCHEMA_DOMAIN}
        schemaParams={schemaParams}
        value={value}
        onApply={onApplyGenerated}
      />
    </Box>
  );

  return (
    <ExpressionEditor
      {...commonProps}
      menu={menu}
      staticValidateField={staticValidateField}
      onChange={handleChange}
      {...props}
    />
  );
};

ExpressionField.propTypes = {
  schemaParams: PropTypes.object.isRequired,
  setInForm: PropTypes.func.isRequired,
  validateField: PropTypes.func.isRequired,
  staticValidateField: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  value: PropTypes.string,
  name: PropTypes.string,
  disabled: PropTypes.bool,
  isOnlyBooleanAllowed: PropTypes.bool,
  forceAdvancedMode: PropTypes.bool,
  // The authoring domain the explain and generate buttons send (their default when null).
  schemaDomain: PropTypes.string,
  // Called after a generated expression is applied (an AI draft the author has not tested yet).
  onGenerated: PropTypes.func,
};

export default ExpressionField;
